import type { MfgRecipeAttachment, MfgRecipeSummary } from "./types/recipes";
import { normalizeSlotLabelsCsv, parseSlotLabelsCsv } from "./slotLabel";

export type AttachmentUiRow = {
  key: string;
  attached_recipe_id: string;
  included_by_default: boolean;
  customer_price_delta: string;
  replaces_slot_labels: string;
  quantity: string;
  notes: string;
  sort_order: number;
};

export function recipeKindById(recipes: readonly MfgRecipeSummary[]): Map<string, MfgRecipeSummary["kind"]> {
  const map = new Map<string, MfgRecipeSummary["kind"]>();
  for (const r of recipes) map.set(r.id, r.kind ?? "assembly");
  return map;
}

export function isVariantAttachment(
  attachedRecipeId: string,
  kindById: ReadonlyMap<string, MfgRecipeSummary["kind"]>,
): boolean {
  return kindById.get(attachedRecipeId) === "variant";
}

export function attachmentRowFromServer(att: MfgRecipeAttachment): AttachmentUiRow {
  return {
    key: att.id,
    attached_recipe_id: att.attached_recipe_id,
    included_by_default: att.included_by_default,
    customer_price_delta:
      typeof att.customer_price_delta === "number" ? String(att.customer_price_delta) : "",
    replaces_slot_labels: Array.isArray(att.replaces_slot_labels)
      ? normalizeSlotLabelsCsv(att.replaces_slot_labels.join(", "))
      : "",
    quantity: String(att.quantity ?? 1),
    notes: att.notes ?? "",
    sort_order: att.sort_order ?? 0,
  };
}

export function emptyAttachmentRow(defaultRecipeId: string, sortOrder: number): AttachmentUiRow {
  return {
    key: crypto.randomUUID(),
    attached_recipe_id: defaultRecipeId,
    included_by_default: false,
    customer_price_delta: "",
    replaces_slot_labels: "",
    quantity: "1",
    notes: "",
    sort_order: sortOrder,
  };
}

export function partitionAttachmentRows(
  rows: readonly AttachmentUiRow[],
  kindById: ReadonlyMap<string, MfgRecipeSummary["kind"]>,
): { variantRows: AttachmentUiRow[]; packageRows: AttachmentUiRow[] } {
  const variantRows: AttachmentUiRow[] = [];
  const packageRows: AttachmentUiRow[] = [];
  for (const row of rows) {
    const id = row.attached_recipe_id.trim();
    if (id && isVariantAttachment(id, kindById)) variantRows.push(row);
    else packageRows.push(row);
  }
  return { variantRows, packageRows };
}

export function parseAttachmentRowsForSave(
  rows: readonly AttachmentUiRow[],
  parentRecipeId: string,
): { ok: true; attachments: Array<{
  attached_recipe_id: string;
  included_by_default: boolean;
  customer_price_delta: number | null;
  replaces_slot_labels: string[];
  quantity: number;
  sort_order: number;
  notes: string;
}> } | { ok: false; error: string } {
  const parsed: Array<{
    attached_recipe_id: string;
    included_by_default: boolean;
    customer_price_delta: number | null;
    replaces_slot_labels: string[];
    quantity: number;
    sort_order: number;
    notes: string;
  }> = [];
  let i = 0;
  for (const row of rows) {
    const attached_recipe_id = row.attached_recipe_id.trim();
    if (!attached_recipe_id) continue;
    if (attached_recipe_id === parentRecipeId) {
      return { ok: false, error: "A link cannot reference the parent recipe." };
    }
    const q = Number(row.quantity);
    const quantity = Number.isFinite(q) && q > 0 ? q : 1;
    const cpdRaw = row.customer_price_delta.trim();
    const customer_price_delta =
      cpdRaw === "" || !Number.isFinite(Number(cpdRaw)) ? null : Number(cpdRaw);
    const labels = parseSlotLabelsCsv(row.replaces_slot_labels);
    parsed.push({
      attached_recipe_id,
      included_by_default: row.included_by_default,
      customer_price_delta,
      replaces_slot_labels: labels,
      quantity,
      sort_order: i,
      notes: row.notes,
    });
    i += 1;
  }
  return { ok: true, attachments: parsed };
}

export function mergeAttachmentPartitions(
  variantRows: readonly AttachmentUiRow[],
  packageRows: readonly AttachmentUiRow[],
): AttachmentUiRow[] {
  return [...variantRows, ...packageRows];
}
